import { getState } from '../../support/helpers/states.ts';
import { IAddress } from '../../support/model/IAddress.ts';
import { ICoordinate } from '../../support/model/ICoordinate.ts';
import { ILocation } from '../../support/model/ILocation.ts';
import { EnvConfiguration } from '../../app/config/EnvConfiguration.ts';

// Common intermediate between Mapbox API responses and our IAddress.
// Each API endpoint builds this via a thin adapter, then parseAddress() parses it uniformly.
export interface IMapboxResult {
    name?: string;       // POI or neighborhood name (kept separate to avoid comma-splitting issues)
    addressText: string; // everything else: "[street,] city, state [zip], country"
}

// Strips the literal string "undefined" that Mapbox sometimes includes in address fields
export const cleanMapboxText = (text: string | undefined) => (text || '').split(',').filter(part => part.trim() !== 'undefined').join(',');

// Adapter for Geocoding v5 API
// The place_name is a pre-formatted string where POI/neighborhood names are the first comma-separated part.
export const fromGeocoding = ({ place_name, place_type }: { place_name: string, place_type: string[] }): IMapboxResult => {
    const cleaned = cleanMapboxText(place_name);
    const hasName = place_type.includes('poi') || place_type.includes('neighborhood');
    if (hasName) {
        const firstComma = cleaned.indexOf(',');
        return { name: cleaned.substring(0, firstComma).trim(), addressText: cleaned.substring(firstComma + 1).trim() };
    }
    return { addressText: cleaned };
};

export class ReverseGeocodingService {
    static inject = () => [EnvConfiguration];
    constructor(private config: EnvConfiguration) {}

    // Parses any Mapbox address text into IAddress. Works uniformly for all result types
    // because Mapbox formats are always most-specific to least-specific, comma-separated:
    //   [street,] city, state [zip], country
    parseAddress = ({ name, addressText }: IMapboxResult): IAddress => {
        const parts = addressText.split(',').map(p => p.trim()).filter(Boolean);
        const country = parts.pop() || '';
        const stateZip = parts.pop() || '';
        const city = parts.pop() || '';
        const street = parts.join(', ');

        const stateZipParts = stateZip.split(' ');
        const lastPart = stateZipParts[stateZipParts.length - 1] || '';
        const zip = /^\d+$/.test(lastPart) ? stateZipParts.pop()! : '';
        const state = getState(stateZipParts.join(' '))?.code || '';

        return { name, street, city, state, zip, country };
    };

    lookup = async ({ lng, lat }: ICoordinate): Promise<ILocation | undefined> => {
        const params = new URLSearchParams();
        params.set('access_token', this.config.MAPBOX_TOKEN);
        params.set('country', 'us');
        params.set('limit', '1');
        params.set('types', 'place,poi,address,country');
        params.set('worldview', 'us');

        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?${params.toString()}`;

        const result = await fetch(url);
        const { features } = await result.json();

        if (!features?.length) {
            console.log('No features returned by mapbox', { lat, lng });
            return undefined;
        }

        const { center, place_name, place_type } = features[0];
        const address = this.parseAddress(fromGeocoding({ place_name, place_type }));

        // ignore if not US
        if (address.country !== 'United States') {
            console.log(`mapbox returned other country: "${place_name}"`, { lat, lng });
            return undefined;
        }

        return {
            address,
            coordinates: {
                lng: center[0],
                lat: center[1]
            }
        };
    };
}
